// WS-75 life: small node-material helpers for the moving things (animals, harness, people).
import * as THREE from 'three/webgpu';
import { diffuseColor, vec4, uniform, materialEmissive, max } from 'three/tsl';

/** a node material with the values of a glTF / classic material (a MeshPhysical* keeps its physical values) */
export function nodeMaterialOf(base) {
  if (base.isNodeMaterial) return base.clone();
  const phys = base.isMeshPhysicalMaterial, m = phys ? new THREE.MeshPhysicalNodeMaterial() : new THREE.MeshStandardNodeMaterial();
  (phys ? THREE.MeshPhysicalMaterial : THREE.MeshStandardMaterial).prototype.copy.call(m, base); // the classic copy: every value, maps included
  m.defines = {}; m.userData = { ...base.userData };
  return m;
}

/**
 * The surface colour (diffuse rgb) from a node, set where three.js sets the diffuse colour, in the colour pass only. Not
 * through colorNode: the shadow pass evaluates a material's colorNode for its alpha, which would run a whole coat or
 * face shader (and bind all its vertex buffers) for a depth. Opacity, alpha test and the material colour's alpha stay
 * three.js's. Use with vertexColors = false when the node reads COLOR_0 itself.
 */
export function diffuseRGB(m, rgb) {
  const base = m.setupDiffuseColor;
  m.setupDiffuseColor = function (builder) {
    base.call(this, builder);
    diffuseColor.assign(vec4(rgb, diffuseColor.a));
  };
  return m;
}

// ---- WS-39 night fill (begin): the moving things' (animals, people, the sleigh and the tack) emissive = albedo x the
// night's own light x lift x the map's night level, so a dark coat keeps its colour and shape at night. The light is the one
// the snow gets (main.js applyTime: the sky fill from above plus the moonlight, / pi as three's Lambert), so a coat is
// lifted in the night's colour and by the night's strength (moon phase, season night). It was a fixed near-white fill
// (0.85, 0.9, 1): ~3x the night light, so a chestnut coat, the sleigh and the guests' clothes stayed warm and day-bright
// beside blue snow (owner, 2026-10-26).

/** The night light a surface facing up gets (linear rgb, / pi), x SHARE / the lifts' 0.3. */
export const NIGHT_LIGHT_FILL = uniform(new THREE.Color());
// How much of the night light a lift of 0.3 (every model's) adds on top of the shaded light: 1 = as much again.
const SHARE = 1;
const moonC = new THREE.Color();
/** main.js applyTime, after every light is set: the hemisphere light (its intensity before the SH takes it) and the moon. */
export function setNightLight(hemi, hemiI, moon) {
  NIGHT_LIGHT_FILL.value.copy(hemi.color).multiplyScalar(hemiI).add(moonC.copy(moon.color).multiplyScalar(moon.intensity)).multiplyScalar(SHARE / 0.3 / Math.PI);
}
/** The emissive node: the material's own emissive + albedo x the night light x k (lift x night level, 0 by day). WP5.1a:
 *  tagged with k (surfNight, a uniform): on a standard material the shared graph (gpu/surface.js) adds the same term in a
 *  uniform If, so the sleigh, the tack and the sleds are no programs of their own. */
export const nightFill = (k) => Object.assign(materialEmissive.add(max(diffuseColor.rgb, 0).mul(NIGHT_LIGHT_FILL).mul(k)), { surfNight: k });
// ---- WS-39 night fill (end)
